/**
 * Store-app detection. Both Capacitor shells append `HausverwalterApp/<kind>`
 * to the WebView user agent (apps/mobile*, capacitor.config.ts). Edge-safe.
 */

export type NativeAppKind = "staff" | "portal";

/** Request header middleware sets for server components; never trusted from clients. */
export const NATIVE_APP_HEADER = "x-native-app";

const NATIVE_APP_UA_RE = /\bHausverwalterApp\/(staff|portal)\b/;

/** Marketing pages the staff app never shows inside its WebView. */
const NATIVE_APP_MARKETING_PREFIXES: readonly string[] = [
  "/preise",
  "/demo",
  "/careers",
  "/marketing",
];

export function readNativeAppKind(
  userAgent: string | null | undefined,
): NativeAppKind | null {
  const kind = NATIVE_APP_UA_RE.exec(userAgent ?? "")?.[1];
  if (kind === "staff") return "staff";
  if (kind === "portal") return "portal";
  return null;
}

/**
 * Hosts the app treats as itself: links there stay in the WebView as a path.
 * Covers customer subdomains, which the app never loads directly.
 */
export function isOwnAppHost(kind: NativeAppKind, hostname: string): boolean {
  const apex = kind === "portal" ? "portalzugang.de" : "hausverwalter.ai";
  const host = hostname.toLowerCase();
  if (host === apex || host === `www.${apex}`) return true;
  return host.endsWith(`.${apex}`) && !host.startsWith("api.");
}

export function isNativeAppMarketingPath(pathname: string): boolean {
  return NATIVE_APP_MARKETING_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Where the store app goes instead of a web-only page, or null to proceed.
 * Logged-out root would render the marketing home; app starts at sign-in instead.
 */
export function nativeAppRedirectPath(input: {
  readonly kind: NativeAppKind;
  readonly pathname: string;
  readonly signedIn: boolean;
  readonly portalHost: boolean;
}): string | null {
  if (input.portalHost) return null;
  if (input.pathname === "/" && !input.signedIn) return "/login";
  if (isNativeAppMarketingPath(input.pathname)) {
    return input.signedIn ? "/home" : "/login";
  }
  return null;
}
